// ============================================================================
// WebformsConfigContext — webforms admin-panel config state
// ============================================================================
// Thin Provider for the webforms admin-panel config state. The default
// config blob (DEFAULT_WEBFORMS_CONFIG) lives in src/lib/defaults.ts and
// AppProviders threads it in as `configInit`.
//
//   webformsConfig  : the full webforms admin config blob
//
// `wfGroups` used to live here too — the active pig group option list. It is
// gone: its only writer was WebformHubAuthenticatedBridge and its only reader
// the hub's own fallback for the value that bridge had just produced, so the
// whole loop was inside one surface and the mirror carried nothing across it
// except a previous mount's list after a failed read.
// ============================================================================
import {createContext, useContext, useState} from 'react';
import type {ReactNode} from 'react';

// Value type INFERRED from the builder below.
//
// `configInit` stays `unknown` ON PURPOSE. The obvious alternative — importing
// DEFAULT_WEBFORMS_CONFIG and writing `configInit: typeof DEFAULT_WEBFORMS_CONFIG`
// — would look like inference but is not: that constant is a plain object
// literal, so its inferred type is the shape of TODAY'S seed data (empty arrays
// become `never[]`, every enum-ish string becomes `string`), and the admin panel
// edits this blob into shapes the seed never had. Pinning it would also give
// this module the upstream dependency its own header says it was written to
// avoid. Both /dailys and the direct Pig Dailys form own their own scoped
// active-group lookups rather than asking App to hydrate this context.
function useWebformsConfigValue(configInit: unknown) {
  const [webformsConfig, setWebformsConfig] = useState(configInit);

  return {
    webformsConfig,
    setWebformsConfig,
  };
}

type WebformsConfigContextValue = ReturnType<typeof useWebformsConfigValue>;

const WebformsConfigContext = createContext<WebformsConfigContextValue | null>(null);

export function WebformsConfigProvider({children, configInit}: {children?: ReactNode; configInit: unknown}) {
  const value = useWebformsConfigValue(configInit);
  return <WebformsConfigContext.Provider value={value}>{children}</WebformsConfigContext.Provider>;
}

// Asserts the provider, and that assertion is what gives every consumer a
// non-null type. Not a new failure mode: the hook used to return `null` outside
// its provider and every consumer destructures the result immediately, so the
// app ALREADY crashed at that exact line with an anonymous
// `TypeError: Cannot destructure property … of null`. Same failure, same point,
// named cause.
export function useWebformsConfig(): WebformsConfigContextValue {
  const value = useContext(WebformsConfigContext);
  if (!value) throw new Error('useWebformsConfig must be used within WebformsConfigProvider');
  return value;
}
